/**
 * /escape — the Concierge® door to activities, tours, attractions,
 *           classes, day trips and experiences worldwide.
 *
 * Founder correction pass 18 Feb 2026 PM-4:
 *   - Wording: "Asking Viator" → "Concierge® is curating this route"
 *   - CTA: "Book on Viator ↗" → "Continue to booking ↗" (with quiet
 *     "Source · Viator" credit underneath)
 *   - Currency: INR by default · ₹ symbol · honest "Live price · final
 *     amount confirmed at checkout" microcopy
 *   - Hero search: two-field "Where to?" + "When?" + date dialog
 *     (Anytime · Specific date · Date range)
 *   - Refine drawer: full category taxonomy + flags + duration + price
 *     + rating + Apply / Clear / Cancel
 *   - Drawer: rich skeleton during load · gallery · highlights · all
 *     sections · honest "Continue to booking ↗" + "Request via
 *     Concierge®"
 *   - Global WhatsApp pill present
 *
 * Founder UX pass 18 Feb 2026 PM-9:
 *   - Visual parity with /enjoy. Shared ConciergeHero +
 *     ConciergeSearchBar atoms now own the hero. Same centred typography,
 *     same brass focus ring, same pill rhythm.
 *   - Post-search Results Toolbar — quick category chips (Tours, Day
 *     Trips, Skip-the-line…), sort menu (Recommended default), Refine
 *     trigger with badge, and removable active filter chips.
 *   - Travellers count visible in the hero pill, persisted with the
 *     search payload.
 *   - Currency selector (default INR, future global launch ready).
 *   - Language filter in the Refine drawer.
 *   - Card front: "Source · Viator" removed. Quiet desk-voice line
 *     "Book directly or ask your Concierge® to arrange it." sits in
 *     its place. Provenance kept in the drawer footer.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    searchEscape, getEscapeProduct,
    getEscapeSignatures, logEscapeIntent,
} from "@/lib/escape-api";
import ConciergeWhatsAppPill from "@/components/global/ConciergeWhatsAppPill";
import ConciergeOnDutyPill from "@/components/global/ConciergeOnDutyPill";
import ConciergeSurfaceMenu from "@/components/global/ConciergeSurfaceMenu";
import CrossSurfaceNav from "@/components/global/CrossSurfaceNav";
import ConciergeHero from "@/components/global/ConciergeHero";
import ConciergeSearchBar from "@/components/global/ConciergeSearchBar";
import { cldOpt } from "@/lib/cloudinary";
import EscapeDiscover from "@/components/escape/EscapeDiscover";
import EscapeTopDestinations from "@/components/escape/EscapeTopDestinations";
import EscapeRequestModal from "@/components/escape/EscapeRequestModal";
import SignatureDetailDrawer from "@/components/escape/SignatureDetailDrawer";
import EscapeFAQ from "@/components/escape/EscapeFAQ";
import EscapeResultsToolbar, {
    QUICK_CHIPS, SORT_OPTIONS,
} from "@/components/escape/EscapeResultsToolbar";
import MemberContextBand from "@/components/MemberContextBand";
import "@/styles/table-surface.css";

/* ── Visual primitives ─────────────────────────────────────────────
 *  Founder UX pass 18 Feb 2026 PM-9: /escape now shares /enjoy's
 *  exact paper/ink/brass tokens via --t-* CSS vars (declared in
 *  /app/frontend/src/styles/table-surface.css). The local T object
 *  is retained as a JS-readable mirror for inline styles, but the
 *  values now match /enjoy character-for-character. */
const T = {
    ink: "#211C15",        // var(--t-ink)
    ink2: "#5C5447",       // var(--t-ink2)
    ink3: "#8A8071",       // var(--t-ink3)
    line: "#E5DCCB",       // var(--t-line)
    line2: "#D8CDB7",      // var(--t-line2)
    card: "#FCFAF4",       // var(--t-card)
    bg: "#F4EFE6",         // var(--t-paper) — page background mirrors /enjoy
    brass: "#8B6914",      // var(--t-brass)
    brassDim: "#C6A560",   // var(--t-brass2)
    brassDeep: "#7d5b21",  // var(--t-brass-deep)
    brassWash: "#EFE6D3",  // var(--t-brass-wash)
};

const STYLES = {
    page: { background: T.bg, minHeight: "100vh", fontFamily: "'Hanken Grotesk', system-ui, sans-serif", color: T.ink },
    container: { maxWidth: 1240, margin: "0 auto", padding: "0 22px" },
    label: { fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", color: T.ink3, fontFamily: "ui-monospace, monospace" },
};

/* ── INR formatter ────────────────────────────────────────────── */
function fmtPrice(p) {
    if (!p || p.amount == null) return null;
    const amt = p.amount;
    if (p.currency === "INR") {
        return "₹" + Math.round(amt).toLocaleString("en-IN");
    }
    // Fallback · still humanise the number, prefix the symbol
    const sym = p.currency === "USD" ? "$" : (p.currency + " ");
    return sym + Math.round(amt).toLocaleString("en-IN");
}

/* ── Date helpers ─────────────────────────────────────────────── */
function isoToday() { return new Date().toISOString().slice(0, 10); }
function isoOffset(daysFromToday) {
    const d = new Date();
    d.setDate(d.getDate() + daysFromToday);
    return d.toISOString().slice(0, 10);
}
function fmtDateShort(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso + "T00:00:00");
        return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
    } catch { return iso; }
}

/* ── Refine state ─────────────────────────────────────────────── */
const EMPTY_REFINE = {
    flags: [],
    categories: [],  // tag-name selections (mapped to flag-equivalents server-side OR sent as tag_ids)
    duration_min: null,
    duration_max: null,
    rating_min: null,
    lowest_price: null,
    highest_price: null,
    include_unrated: false,
    language: null,   // null = page default · explicit override goes here
    // Wave B-2 widening · 19 Feb 2026
    region: null,        // tag · prefixes the search query
    landmark: null,      // tag · prefixes the search query
    family_friendly: false,
    infants_welcome: false,
};

const CATEGORY_LIST = [
    "Tours", "Attractions", "Day Trips", "Food & Drink", "Culture",
    "Classes", "Adventure", "Nature & Wildlife", "Water Activities",
    "Family", "Wellness", "Private Tours", "Skip-the-line", "Local Experiences",
];

const FLAGS_AVAILABLE = [
    { id: "FREE_CANCELLATION", label: "Free cancellation" },
    { id: "SKIP_THE_LINE", label: "Skip the line" },
    { id: "PRIVATE_TOUR", label: "Private tour" },
    { id: "LIKELY_TO_SELL_OUT", label: "Likely to sell out" },
];

const DURATION_PRESETS = [
    { id: "lt60", label: "Under 1 hour", min: 0, max: 60 },
    { id: "1to3", label: "1 – 3 hours", min: 60, max: 180 },
    { id: "halfday", label: "Half-day (3 – 6 h)", min: 180, max: 360 },
    { id: "fullday", label: "Full day (6 – 12 h)", min: 360, max: 720 },
    { id: "multi", label: "Multi-day (12 h+)", min: 720, max: null },
];

const PRICE_PRESETS = [
    { id: "under5k",      label: "Under ₹5,000",       lowest: null,  highest: 5000 },
    { id: "5to10k",       label: "₹5,000 – 10,000",    lowest: 5000,  highest: 10000 },
    { id: "10to25k",      label: "₹10,000 – 25,000",   lowest: 10000, highest: 25000 },
    { id: "25kplus",      label: "₹25,000+",           lowest: 25000, highest: null },
];

const RATING_PRESETS = [
    { id: "4plus", label: "★ 4.0 +", value: 4.0 },
    { id: "45plus", label: "★ 4.5 +", value: 4.5 },
];

/* ── Currency · India-anchored default. The dropdown is wired
 *    against Viator's accepted currency set so we can light up new
 *    markets without code changes. Symbols are display-only — the
 *    backend forces INR if the param is missing. */
const CURRENCY_OPTIONS = [
    { code: "INR", symbol: "₹",   label: "INR · Indian Rupee" },
    { code: "USD", symbol: "$",   label: "USD · US Dollar" },
    { code: "EUR", symbol: "€",   label: "EUR · Euro" },
    { code: "GBP", symbol: "£",   label: "GBP · British Pound" },
    { code: "AED", symbol: "AED", label: "AED · UAE Dirham" },
    { code: "SGD", symbol: "S$",  label: "SGD · Singapore Dollar" },
    { code: "AUD", symbol: "A$",  label: "AUD · Australian Dollar" },
];

const CURRENCY_LS_KEY = "lc_escape_currency_v1";

/* ── Language options · Viator's supported product-content
 *    languages. Default en-US per founder ruling. */
const LANGUAGE_OPTIONS = [
    { code: "en-US", label: "English (US)" },
    { code: "en-GB", label: "English (UK)" },
    { code: "es-ES", label: "Spanish" },
    { code: "fr-FR", label: "French" },
    { code: "de-DE", label: "German" },
    { code: "it-IT", label: "Italian" },
    { code: "pt-PT", label: "Portuguese" },
    { code: "ja-JP", label: "Japanese" },
    // Founder ruling 19 Feb 2026 Wave B-2 · widen the Refine language
    // dimension. Members from /club Hindi-belt and Gulf cohorts
    // explicitly requested these. Viator supports all four.
    { code: "zh-CN", label: "Mandarin" },
    { code: "ar-AE", label: "Arabic" },
    { code: "hi-IN", label: "Hindi" },
    { code: "ko-KR", label: "Korean" },
];

/* ── Wave B-2 widening · region · landmark · family ──────────── */
const REGION_OPTIONS = [
    { id: "europe",      label: "Europe" },
    { id: "asia",        label: "Asia & Pacific" },
    { id: "middle-east", label: "Middle East" },
    { id: "north-america", label: "North America" },
    { id: "latin-america", label: "Latin America & Caribbean" },
    { id: "africa",      label: "Africa & Indian Ocean" },
];

const LANDMARK_OPTIONS = [
    "Eiffel Tower", "Colosseum", "Burj Khalifa", "Sagrada Família",
    "Statue of Liberty", "Tokyo Skytree", "Stonehenge", "Acropolis",
    "Grand Canyon", "Vatican Museums",
];

/* ── Travellers popover · adults + children ──────────────────── */
function TravellersStepper({ label, hint, val, set, min, max, testid }) {
    return (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0" }}>
            <div>
                <div style={{ fontSize: 14, color: T.ink }}>{label}</div>
                {hint && <div style={{ fontSize: 11, color: T.ink3 }}>{hint}</div>}
            </div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 12 }}>
                <button
                    data-testid={`${testid}-dec`}
                    type="button"
                    onClick={() => set(Math.max(min, val - 1))}
                    disabled={val <= min}
                    style={{
                        width: 30, height: 30, borderRadius: 999,
                        background: T.card, border: `1px solid ${T.line2}`,
                        color: T.ink, cursor: val <= min ? "not-allowed" : "pointer",
                        fontSize: 16, fontWeight: 500, lineHeight: 1,
                        opacity: val <= min ? 0.4 : 1,
                    }}
                >−</button>
                <span
                    data-testid={`${testid}-value`}
                    style={{ fontSize: 15, minWidth: 18, textAlign: "center" }}
                >{val}</span>
                <button
                    data-testid={`${testid}-inc`}
                    type="button"
                    onClick={() => set(Math.min(max, val + 1))}
                    disabled={val >= max}
                    style={{
                        width: 30, height: 30, borderRadius: 999,
                        background: T.card, border: `1px solid ${T.line2}`,
                        color: T.ink, cursor: val >= max ? "not-allowed" : "pointer",
                        fontSize: 16, fontWeight: 500, lineHeight: 1,
                        opacity: val >= max ? 0.4 : 1,
                    }}
                >+</button>
            </div>
        </div>
    );
}

function TravellersPopover({ open, value, onChange, onClose }) {
    // Internal state initialises from `value` on every mount. The
    // popover unmounts when closed, so opening again re-reads the
    // latest value — no useEffect sync needed.
    const [adults, setAdults] = useState(value?.adults ?? 2);
    const [children, setChildren] = useState(value?.children ?? 0);
    if (!open) return null;
    function apply() {
        onChange({ adults, children });
        onClose();
    }
    return (
        <div
            data-testid="escape-travellers-popover"
            style={{
                position: "absolute", top: "calc(100% + 8px)", right: 0, zIndex: 30,
                background: T.card, border: `1px solid ${T.line2}`, borderRadius: 14,
                padding: 16, width: "min(320px, 92vw)",
                boxShadow: "0 12px 32px rgba(33,28,21,0.12)",
            }}
            onClick={(e) => e.stopPropagation()}
        >
            <TravellersStepper
                label="Adults" hint="Age 13+"
                val={adults} set={setAdults}
                min={1} max={20}
                testid="escape-travellers-adults"
            />
            <div style={{ height: 1, background: T.line, margin: "4px 0" }} />
            <TravellersStepper
                label="Children" hint="Age 0–12"
                val={children} set={setChildren}
                min={0} max={10}
                testid="escape-travellers-children"
            />
            <p style={{ fontSize: 11.5, color: T.ink3, marginTop: 12, lineHeight: 1.45 }}>
                Final ages, infant policies and traveller details are confirmed on the partner booking page.
            </p>
            <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
                <button
                    data-testid="escape-travellers-cancel"
                    onClick={onClose}
                    style={{ background: "transparent", color: T.ink2, border: `1px solid ${T.line2}`, borderRadius: 10, padding: "8px 14px", fontSize: 13, cursor: "pointer" }}
                >Cancel</button>
                <button
                    data-testid="escape-travellers-apply"
                    onClick={apply}
                    style={{ background: T.ink, color: T.card, border: 0, borderRadius: 10, padding: "8px 16px", fontSize: 13, cursor: "pointer" }}
                >Apply</button>
            </div>
        </div>
    );
}

/* ── Currency dropdown · sits in the top header ──────────────── */
function CurrencyDropdown({ value, onChange }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        function close(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);
    const cur = CURRENCY_OPTIONS.find((c) => c.code === value) || CURRENCY_OPTIONS[0];
    return (
        <div ref={ref} style={{ position: "relative" }}>
            <button
                type="button"
                data-testid="escape-currency-trigger"
                onClick={() => setOpen((v) => !v)}
                style={{
                    background: "transparent", color: T.ink2,
                    border: `1px solid ${T.line2}`, borderRadius: 999,
                    padding: "5px 12px", fontSize: 12, cursor: "pointer",
                    fontFamily: "inherit",
                    display: "inline-flex", alignItems: "center", gap: 6,
                }}
            >
                <span style={{ fontWeight: 600 }}>{cur.symbol}</span>
                <span>{cur.code}</span>
                <span style={{ fontSize: 9, color: T.ink3 }} aria-hidden="true">▾</span>
            </button>
            {open && (
                <div
                    data-testid="escape-currency-menu"
                    role="menu"
                    style={{
                        position: "absolute", top: "calc(100% + 6px)", right: 0,
                        background: T.card, border: `1px solid ${T.line2}`,
                        borderRadius: 12, padding: 6, minWidth: 220,
                        boxShadow: "0 12px 32px rgba(33,28,21,0.12)", zIndex: 40,
                    }}
                >
                    {CURRENCY_OPTIONS.map((c) => {
                        const isActive = c.code === cur.code;
                        return (
                            <button
                                key={c.code}
                                type="button"
                                role="menuitem"
                                data-testid={`escape-currency-${c.code.toLowerCase()}`}
                                onClick={() => { onChange?.(c.code); setOpen(false); }}
                                style={{
                                    width: "100%", textAlign: "left",
                                    padding: "8px 12px", borderRadius: 8,
                                    background: isActive ? T.brassWash : "transparent",
                                    color: T.ink, border: 0, cursor: "pointer",
                                    fontSize: 13, fontFamily: "inherit",
                                    display: "flex", justifyContent: "space-between", gap: 12,
                                    fontWeight: isActive ? 600 : 400,
                                }}
                            >
                                <span>{c.label}</span>
                                <span style={{ color: T.ink3 }}>{c.symbol}</span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/* ── Date popover ─────────────────────────────────────────────── */
function DatePopover({ open, value, onChange, onClose }) {
    const [tab, setTab] = useState(value?.mode || "anytime");
    const [singleDate, setSingleDate] = useState(value?.startDate || isoOffset(7));
    const [rangeStart, setRangeStart] = useState(value?.startDate || isoOffset(7));
    const [rangeEnd, setRangeEnd] = useState(value?.endDate || isoOffset(14));
    if (!open) return null;
    function apply() {
        if (tab === "anytime") onChange({ mode: "anytime", startDate: null, endDate: null });
        else if (tab === "single") onChange({ mode: "single", startDate: singleDate, endDate: singleDate });
        else onChange({ mode: "range", startDate: rangeStart, endDate: rangeEnd });
        onClose();
    }
    return (
        <div
            data-testid="escape-date-popover"
            style={{
                position: "absolute", top: "calc(100% + 8px)", left: 0, zIndex: 30,
                background: T.card, border: `1px solid ${T.line2}`, borderRadius: 14,
                padding: 16, width: "min(360px, 92vw)",
                maxWidth: "calc(100vw - 32px)",
                boxShadow: "0 12px 32px rgba(33,28,21,0.12)",
            }}
            onClick={(e) => e.stopPropagation()}
        >
            <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
                {[
                    { id: "anytime", label: "Anytime" },
                    { id: "single", label: "Specific date" },
                    { id: "range", label: "Date range" },
                ].map((o) => (
                    <button
                        key={o.id}
                        data-testid={`escape-date-tab-${o.id}`}
                        onClick={() => setTab(o.id)}
                        style={{
                            background: tab === o.id ? T.ink : "transparent",
                            color: tab === o.id ? T.card : T.ink2,
                            border: `1px solid ${tab === o.id ? T.ink : T.line2}`,
                            borderRadius: 999, padding: "6px 12px",
                            fontSize: 12, cursor: "pointer", fontFamily: "inherit",
                        }}
                    >
                        {o.label}
                    </button>
                ))}
            </div>
            {tab === "anytime" && (
                <p style={{ fontSize: 13, color: T.ink3, lineHeight: 1.5, margin: 0 }}>
                    We&apos;ll show experiences available throughout the year. You can refine the date on the partner site at checkout.
                </p>
            )}
            {tab === "single" && (
                <label style={{ display: "block", fontSize: 12, color: T.ink3 }}>
                    Date
                    <input
                        type="date"
                        data-testid="escape-date-single-input"
                        value={singleDate}
                        min={isoToday()}
                        onChange={(e) => setSingleDate(e.target.value)}
                        style={{ display: "block", marginTop: 6, padding: "8px 10px", border: `1px solid ${T.line2}`, borderRadius: 10, fontSize: 14, width: "100%", boxSizing: "border-box" }}
                    />
                </label>
            )}
            {tab === "range" && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <label style={{ fontSize: 12, color: T.ink3 }}>
                        From
                        <input
                            type="date"
                            data-testid="escape-date-range-from"
                            value={rangeStart}
                            min={isoToday()}
                            onChange={(e) => setRangeStart(e.target.value)}
                            style={{ display: "block", marginTop: 6, padding: "8px 10px", border: `1px solid ${T.line2}`, borderRadius: 10, fontSize: 14, width: "100%", boxSizing: "border-box" }}
                        />
                    </label>
                    <label style={{ fontSize: 12, color: T.ink3 }}>
                        To
                        <input
                            type="date"
                            data-testid="escape-date-range-to"
                            value={rangeEnd}
                            min={rangeStart || isoToday()}
                            onChange={(e) => setRangeEnd(e.target.value)}
                            style={{ display: "block", marginTop: 6, padding: "8px 10px", border: `1px solid ${T.line2}`, borderRadius: 10, fontSize: 14, width: "100%", boxSizing: "border-box" }}
                        />
                    </label>
                </div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
                <button
                    data-testid="escape-date-cancel"
                    onClick={onClose}
                    style={{ background: "transparent", color: T.ink2, border: `1px solid ${T.line2}`, borderRadius: 10, padding: "8px 14px", fontSize: 13, cursor: "pointer" }}
                >Cancel</button>
                <button
                    data-testid="escape-date-apply"
                    onClick={apply}
                    style={{ background: T.ink, color: T.card, border: 0, borderRadius: 10, padding: "8px 16px", fontSize: 13, cursor: "pointer" }}
                >Apply</button>
            </div>
        </div>
    );
}

/* ── Top bar ──────────────────────────────────────────────────── */
function TopBar({ currency, onCurrencyChange }) {
    return (
        <header
            data-testid="escape-header"
            style={{
                borderBottom: `1px solid ${T.line}`,
                background: T.bg, position: "sticky", top: 0, zIndex: 20,
            }}
        >
            <div style={{ ...STYLES.container, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 22px", gap: 10, flexWrap: "nowrap", minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
                    <Link to="/" style={{ textDecoration: "none", color: T.ink, display: "inline-flex", alignItems: "baseline", gap: 10, minWidth: 0 }}>
                        <span className="t-serif" style={{ fontSize: 22, fontWeight: 500, letterSpacing: "0.02em" }}>/escape</span>
                        <span
                            data-testid="escape-header-by-concierge"
                            className="lc-mobile-hide-on-narrow"
                            style={{ ...STYLES.label, color: T.ink3, whiteSpace: "nowrap" }}
                        >by Concierge®</span>
                    </Link>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                    <CurrencyDropdown value={currency} onChange={onCurrencyChange} />
                    <ConciergeOnDutyPill context="escape" />
                    {/* Cross-surface desktop nav · founder ruling 19 Feb 2026
                        late evening · quiet brass dot under the current
                        surface link (here that's /escape). Hidden on mobile;
                        the hamburger menu takes over. */}
                    <CrossSurfaceNav testidPrefix="escape-header-nav" size="compact" />
                    {/* Mobile-only · compact "more" menu icon */}
                    <ConciergeSurfaceMenu />
                </div>
            </div>
        </header>
    );
}

/* ── EscapeSearchPill · the bar itself (used in landing hero AND
 *    the compact post-search strip). Owns the When? + Travellers
 *    triggers + their popovers. ConciergeSearchBar provides the
 *    pill chrome; this component fills the secondaryField slot.   */
function EscapeSearchPill({
    q, setQ,
    dateValue, setDateValue,
    travellers, setTravellers,
    onSubmit, loading, compact = false,
}) {
    const [dateOpen, setDateOpen] = useState(false);
    const [travellersOpen, setTravellersOpen] = useState(false);
    const dateLabel = useMemo(() => {
        if (!dateValue || dateValue.mode === "anytime") return "Anytime";
        if (dateValue.mode === "single") return fmtDateShort(dateValue.startDate);
        if (dateValue.mode === "range") return `${fmtDateShort(dateValue.startDate)} – ${fmtDateShort(dateValue.endDate)}`;
        return "Anytime";
    }, [dateValue]);
    const travellersLabel = useMemo(() => {
        const a = travellers?.adults ?? 2;
        const c = travellers?.children ?? 0;
        const total = a + c;
        return `${total} traveller${total === 1 ? "" : "s"}`;
    }, [travellers]);

    const triggerStyle = {
        padding: compact ? "6px 14px" : "10px 18px",
        background: "transparent",
        border: 0,
        textAlign: "left",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        fontFamily: "inherit",
        minWidth: 0,
    };
    const triggerLabelStyle = {
        fontSize: 11, fontWeight: 600, color: T.ink, letterSpacing: "0.02em",
    };
    const triggerValueStyle = {
        fontSize: compact ? 13.5 : 15, color: T.ink, marginTop: 2,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
    };

    return (
        <ConciergeSearchBar
            value={q}
            onChange={setQ}
            onSubmit={onSubmit}
            placeholder="Search for a place or activity"
            submitLabel={compact ? "Search" : "Find escapes"}
            loadingLabel="Curating…"
            loading={loading}
            testid="escape-search-bar"
            compact={compact}
            secondaryField={
                <>
                    <div style={{ position: "relative" }}>
                        <button
                            type="button"
                            data-testid="escape-date-trigger"
                            onClick={(e) => { e.stopPropagation(); setDateOpen((v) => !v); setTravellersOpen(false); }}
                            style={triggerStyle}
                        >
                            <span style={triggerLabelStyle}>When?</span>
                            <span style={triggerValueStyle}>{dateLabel}</span>
                        </button>
                        <DatePopover
                            open={dateOpen}
                            value={dateValue}
                            onChange={setDateValue}
                            onClose={() => setDateOpen(false)}
                        />
                    </div>
                    <div aria-hidden="true" style={{ width: 1, alignSelf: "stretch", margin: "10px 0", background: T.line }} />
                    <div style={{ position: "relative" }}>
                        <button
                            type="button"
                            data-testid="escape-travellers-trigger"
                            onClick={(e) => { e.stopPropagation(); setTravellersOpen((v) => !v); setDateOpen(false); }}
                            style={triggerStyle}
                        >
                            <span style={triggerLabelStyle}>Travellers</span>
                            <span style={triggerValueStyle}>{travellersLabel}</span>
                        </button>
                        <TravellersPopover
                            open={travellersOpen}
                            value={travellers}
                            onChange={setTravellers}
                            onClose={() => setTravellersOpen(false)}
                        />
                    </div>
                </>
            }
        />
    );
}

/* ── Hero · centered /enjoy-parity ──────────────────────────── */
function HeroSearch({
    q, setQ, dateValue, setDateValue,
    travellers, setTravellers,
    onSubmit, loading, onOpenDiscover, onAskConcierge,
}) {
    return (
        <ConciergeHero
            testid="escape-hero"
            eyebrow="Concierge® · Escape"
            headline="Where would you like to"
            headlineTail="escape?"
            subtitle={<>Tours, activities, attractions and day trips&mdash;with the Concierge® beside you.</>}
            maxWidth={820}
        >
            <EscapeSearchPill
                q={q}
                setQ={setQ}
                dateValue={dateValue}
                setDateValue={setDateValue}
                travellers={travellers}
                setTravellers={setTravellers}
                onSubmit={onSubmit}
                loading={loading}
            />
            <div
                data-testid="escape-hero-secondary-row"
                className="lc-hero-cta-row"
                style={{
                    display: "flex", justifyContent: "center", alignItems: "center",
                    gap: 18, marginTop: 18, flexWrap: "wrap",
                }}
            >
                <button
                    type="button"
                    data-testid="escape-discover-open"
                    onClick={onOpenDiscover}
                    style={{
                        background: "transparent",
                        color: T.ink,
                        border: `1.5px solid ${T.ink}`,
                        borderRadius: 999,
                        padding: "9px 18px",
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: "pointer",
                        fontFamily: "inherit",
                    }}
                >
                    Browse escapes
                </button>
                <button
                    type="button"
                    onClick={() => onAskConcierge?.()}
                    data-testid="escape-hero-concierge-cta"
                    style={{
                        background: "transparent",
                        color: T.brass,
                        border: `1px solid ${T.brass}`,
                        borderRadius: 999,
                        padding: "10px 22px",
                        fontSize: 13.5,
                        fontWeight: 500,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        transition: "all 0.15s",
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = T.brass;
                        e.currentTarget.style.color = T.bg;
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.color = T.brass;
                    }}
                >
                    Ask the Concierge®
                </button>
            </div>
            <p
                data-testid="escape-hero-secondary-copy"
                className="t-serif"
                style={{
                    marginTop: 14,
                    fontSize: "clamp(11.5px, 1.2vw, 13.5px)",
                    color: T.ink3,
                    fontStyle: "italic",
                    textAlign: "center",
                }}
            >
                Book directly or ask your Concierge® to arrange it.
            </p>
        </ConciergeHero>
    );
}

/* ── Compact post-search strip · sticky-feeling search bar that
 *    keeps Where/When/Travellers visible while results scroll.    */
function ResultsSearchStrip({
    q, setQ, dateValue, setDateValue,
    travellers, setTravellers, onSubmit, loading,
}) {
    return (
        <section
            data-testid="escape-results-search-strip"
            style={{ padding: "20px 22px 0" }}
        >
            <div style={STYLES.container}>
                <EscapeSearchPill
                    q={q}
                    setQ={setQ}
                    dateValue={dateValue}
                    setDateValue={setDateValue}
                    travellers={travellers}
                    setTravellers={setTravellers}
                    onSubmit={onSubmit}
                    loading={loading}
                    compact
                />
            </div>
        </section>
    );
}

/* ── Popular prompts ──────────────────────────────────────────── */
const POPULAR_PROMPTS = [
    "Paris food tour", "Dubai desert safari", "Tokyo cooking class",
    "London Harry Potter tour", "Bali family activities",
    "Rome Vatican skip the line", "Iceland northern lights", "Cape Town wine tour",
];

function PopularPrompts({ onPick }) {
    return (
        <section data-testid="escape-popular-prompts" style={{ padding: "10px 22px 56px" }}>
            <div style={STYLES.container}>
                <div style={{ ...STYLES.label, marginBottom: 14 }}>Try a search</div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {POPULAR_PROMPTS.map((p) => (
                        <button
                            key={p}
                            type="button"
                            data-testid={`escape-popular-${p.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={() => onPick?.(p)}
                            style={{
                                background: T.card, border: `1px solid ${T.line2}`,
                                borderRadius: 999, padding: "7px 14px", fontSize: 13,
                                color: T.ink2, cursor: "pointer", fontFamily: "inherit",
                            }}
                        >
                            {p}
                        </button>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ── Refine search drawer · full taxonomy ─────────────────────── */
const RefinePill = ({ active, onClick, children, testid }) => (
    <button
        type="button"
        data-testid={testid}
        onClick={onClick}
        style={{
            background: active ? T.ink : T.card,
            color: active ? T.card : T.ink2,
            border: `1px solid ${active ? T.ink : T.line2}`,
            borderRadius: 999, padding: "7px 14px",
            fontSize: 13, cursor: "pointer", fontFamily: "inherit",
        }}
    >
        {children}
    </button>
);

function RefineDrawer({ open, draft, setDraft, onApply, onClear, onClose }) {
    if (!open) return null;
    function toggleFlag(id) {
        setDraft((d) => {
            const has = (d.flags || []).includes(id);
            return { ...d, flags: has ? d.flags.filter((x) => x !== id) : [...(d.flags || []), id] };
        });
    }
    function toggleCategory(name) {
        setDraft((d) => {
            const has = (d.categories || []).includes(name);
            return { ...d, categories: has ? d.categories.filter((x) => x !== name) : [...(d.categories || []), name] };
        });
    }
    return (
        <div
            data-testid="escape-refine-drawer"
            role="dialog" aria-label="Refine search"
            style={{
                position: "fixed", inset: 0, zIndex: 50,
                background: "rgba(10,10,12,0.34)",
                display: "flex", justifyContent: "flex-end",
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <aside style={{
                width: "min(520px, 100%)", height: "100%", background: T.bg,
                overflowY: "auto", padding: "26px 26px 120px",
                borderLeft: `1px solid ${T.line}`,
            }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                    <div className="t-serif" style={{ fontSize: 22 }}>Refine search</div>
                    <button
                        data-testid="escape-refine-close"
                        onClick={onClose}
                        style={{ background: "transparent", border: 0, fontSize: 22, color: T.ink2, cursor: "pointer" }}
                    >×</button>
                </div>

                <Section title="Category">
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {CATEGORY_LIST.map((c) => (
                            <RefinePill
                                key={c}
                                testid={`escape-refine-category-${c.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "")}`}
                                active={(draft.categories || []).includes(c)}
                                onClick={() => toggleCategory(c)}
                            >{c}</RefinePill>
                        ))}
                    </div>
                </Section>

                <Section title="Experience style">
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {FLAGS_AVAILABLE.map((f) => (
                            <RefinePill
                                key={f.id}
                                testid={`escape-refine-flag-${f.id.toLowerCase()}`}
                                active={(draft.flags || []).includes(f.id)}
                                onClick={() => toggleFlag(f.id)}
                            >{f.label}</RefinePill>
                        ))}
                    </div>
                </Section>

                <Section title="Duration">
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {DURATION_PRESETS.map((p) => (
                            <RefinePill
                                key={p.id}
                                testid={`escape-refine-duration-${p.id}`}
                                active={draft.duration_min === p.min && draft.duration_max === p.max}
                                onClick={() => setDraft((d) => ({ ...d, duration_min: p.min, duration_max: p.max }))}
                            >{p.label}</RefinePill>
                        ))}
                    </div>
                </Section>

                <Section title="Price (per person, INR)">
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {PRICE_PRESETS.map((p) => (
                            <RefinePill
                                key={p.id}
                                testid={`escape-refine-price-${p.id}`}
                                active={draft.lowest_price === p.lowest && draft.highest_price === p.highest}
                                onClick={() => setDraft((d) => ({ ...d, lowest_price: p.lowest, highest_price: p.highest }))}
                            >{p.label}</RefinePill>
                        ))}
                    </div>
                </Section>

                <Section title="Quality">
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {RATING_PRESETS.map((p) => (
                            <RefinePill
                                key={p.id}
                                testid={`escape-refine-rating-${p.id}`}
                                active={draft.rating_min === p.value}
                                onClick={() => setDraft((d) => ({ ...d, rating_min: p.value }))}
                            >{p.label}</RefinePill>
                        ))}
                    </div>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16, fontSize: 13, color: T.ink2, cursor: "pointer" }}>
                        <input
                            type="checkbox"
                            data-testid="escape-refine-include-unrated"
                            checked={!!draft.include_unrated}
                            onChange={(e) => setDraft((d) => ({ ...d, include_unrated: e.target.checked }))}
                        />
                        Include unrated / new listings
                    </label>
                </Section>

                {/* ─────── Wave B-2 widening · 19 Feb 2026 PM ─────── */}
                <Section title="Region">
                    <p style={{ fontSize: 12.5, color: T.ink3, margin: "0 0 10px", lineHeight: 1.5 }}>
                        Narrow the search to a continental band. Combine with destination for tighter shortlists.
                    </p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {REGION_OPTIONS.map((r) => (
                            <RefinePill
                                key={r.id}
                                testid={`escape-refine-region-${r.id}`}
                                active={draft.region === r.id}
                                onClick={() => setDraft((d) => ({
                                    ...d,
                                    region: d.region === r.id ? null : r.id,
                                }))}
                            >{r.label}</RefinePill>
                        ))}
                    </div>
                </Section>

                <Section title="Anchor landmark">
                    <p style={{ fontSize: 12.5, color: T.ink3, margin: "0 0 10px", lineHeight: 1.5 }}>
                        Pin the search around a single landmark — useful for &ldquo;skip-the-line near the Colosseum&rdquo; style asks.
                    </p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {LANDMARK_OPTIONS.map((lm) => (
                            <RefinePill
                                key={lm}
                                testid={`escape-refine-landmark-${lm.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                                active={draft.landmark === lm}
                                onClick={() => setDraft((d) => ({
                                    ...d,
                                    landmark: d.landmark === lm ? null : lm,
                                }))}
                            >{lm}</RefinePill>
                        ))}
                    </div>
                </Section>

                <Section title="Travellers">
                    <p style={{ fontSize: 12.5, color: T.ink3, margin: "0 0 10px", lineHeight: 1.5 }}>
                        Make sure results suit who&rsquo;s going. The Concierge® also reviews safety notes when relevant.
                    </p>
                    <label
                        style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: T.ink2, cursor: "pointer", marginBottom: 8 }}
                    >
                        <input
                            type="checkbox"
                            data-testid="escape-refine-family-friendly"
                            checked={!!draft.family_friendly}
                            onChange={(e) => setDraft((d) => ({ ...d, family_friendly: e.target.checked }))}
                        />
                        Family-friendly (children welcome)
                    </label>
                    <label
                        style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: T.ink2, cursor: "pointer" }}
                    >
                        <input
                            type="checkbox"
                            data-testid="escape-refine-infants-welcome"
                            checked={!!draft.infants_welcome}
                            onChange={(e) => setDraft((d) => ({ ...d, infants_welcome: e.target.checked }))}
                        />
                        Infant carriers welcome (under 2)
                    </label>
                </Section>

                <Section title="Tour language">
                    <p style={{ fontSize: 12.5, color: T.ink3, margin: "0 0 10px", lineHeight: 1.5 }}>
                        Preferred narration / commentary language. Provider availability varies.
                    </p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {LANGUAGE_OPTIONS.map((lng) => (
                            <RefinePill
                                key={lng.code}
                                testid={`escape-refine-language-${lng.code.toLowerCase()}`}
                                active={draft.language === lng.code}
                                onClick={() => setDraft((d) => ({
                                    ...d,
                                    language: d.language === lng.code ? null : lng.code,
                                }))}
                            >{lng.label}</RefinePill>
                        ))}
                    </div>
                </Section>

                <div style={{ display: "flex", gap: 10, position: "sticky", bottom: 0, paddingTop: 16, background: T.bg }}>
                    <button
                        data-testid="escape-refine-apply"
                        onClick={() => { onApply?.(); onClose?.(); }}
                        style={{
                            background: T.ink, color: T.card, border: 0, borderRadius: 14,
                            padding: "12px 22px", fontSize: 14, fontWeight: 500, cursor: "pointer", flex: 1,
                        }}
                    >Apply filters</button>
                    <button
                        data-testid="escape-refine-clear"
                        onClick={() => onClear?.()}
                        style={{
                            background: "transparent", color: T.ink2,
                            border: `1px solid ${T.line2}`, borderRadius: 14,
                            padding: "12px 18px", fontSize: 14, cursor: "pointer",
                        }}
                    >Clear all</button>
                    <button
                        data-testid="escape-refine-cancel"
                        onClick={onClose}
                        style={{
                            background: "transparent", color: T.ink2,
                            border: 0, padding: "12px 12px", fontSize: 14, cursor: "pointer",
                        }}
                    >Cancel</button>
                </div>
            </aside>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div style={{ marginBottom: 24 }}>
            <div style={{ ...STYLES.label, marginBottom: 10 }}>{title}</div>
            {children}
        </div>
    );
}

/* ── Card ─────────────────────────────────────────────────────── */
function EscapeCard({ card, onOpen }) {
    const rating = card.rating != null ? card.rating.toFixed(1) : null;
    const priceStr = fmtPrice(card.from_price);
    return (
        <article
            data-testid={`escape-card-${card.product_code}`}
            onClick={() => onOpen?.(card)}
            style={{
                background: T.card, border: `1px solid ${T.line2}`, borderRadius: 14,
                overflow: "hidden", cursor: "pointer",
                display: "flex", flexDirection: "column",
                transition: "border-color 0.18s ease, transform 0.18s ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.brass; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.line2; }}
        >
            <div
                style={{
                    width: "100%", aspectRatio: "16/10",
                    background: card.hero_image_url
                        ? `url(${card.hero_image_url}) center/cover no-repeat`
                        : T.line,
                }}
                aria-label={card.title}
            />
            <div style={{ padding: "14px 16px 18px", display: "flex", flexDirection: "column", gap: 8 }}>
                <h3 className="t-serif" style={{ fontSize: 17, margin: 0, lineHeight: 1.2, color: T.ink }}>
                    {card.title}
                </h3>
                {(card.destination_name || card.country) && (
                    <div style={{ fontSize: 12, color: T.ink3 }}>
                        {[card.destination_name, card.country].filter(Boolean).join(" · ")}
                    </div>
                )}
                <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 13, color: T.ink2 }}>
                    {rating != null && (
                        <span>★ {rating}{" "}
                            {card.review_count != null && (
                                <span style={{ color: T.ink3 }}>({card.review_count.toLocaleString("en-IN")})</span>
                            )}
                        </span>
                    )}
                    {card.duration_text && <span>⌖ {card.duration_text}</span>}
                </div>
                {priceStr && (
                    <div style={{ fontSize: 14, color: T.ink, marginTop: 2 }}>
                        From <strong>{priceStr}</strong>
                    </div>
                )}
                {!priceStr && (
                    <div style={{ fontSize: 12, color: T.ink3, marginTop: 2 }}>
                        Price available on partner site
                    </div>
                )}
                {(card.badges || []).length > 0 && (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                        {card.badges.slice(0, 3).map((b) => (
                            <span
                                key={b}
                                style={{
                                    fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase",
                                    color: T.brass, border: `1px solid ${T.brassDim}`,
                                    borderRadius: 999, padding: "2px 8px",
                                }}
                            >
                                {b.replace(/_/g, " ").toLowerCase()}
                            </span>
                        ))}
                    </div>
                )}
                {/* Founder ruling 18 Feb 2026 PM-9 · the loud "Source · Viator"
                    label is OFF the front of the card. Provenance stays in
                    the drawer footer where it belongs. Replaced here with
                    the quiet desk-voice line · "Book directly or ask your
                    Concierge® to arrange it." */}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginTop: 8,
                        gap: 10,
                    }}
                >
                    <span
                        data-testid="escape-card-secondary-copy"
                        style={{
                            fontSize: 11.5,
                            color: T.ink3,
                            fontStyle: "italic",
                            lineHeight: 1.35,
                            flex: 1,
                            minWidth: 0,
                        }}
                    >
                        Book directly or ask your Concierge® to arrange it.
                    </span>
                    <span
                        data-testid="escape-card-view-details"
                        style={{ fontSize: 12, color: T.ink2, whiteSpace: "nowrap", fontWeight: 500 }}
                    >View details →</span>
                </div>
            </div>
        </article>
    );
}

/* ── Detail drawer with rich skeleton ─────────────────────────── */
function DrawerSkeleton() {
    return (
        <div data-testid="escape-drawer-skeleton" style={{ padding: "0 32px 40px" }}>
            <div style={{ width: "100%", aspectRatio: "16/9", background: T.line, borderRadius: 12 }} />
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} style={{ width: 80, height: 56, background: T.line, borderRadius: 8 }} />
                ))}
            </div>
            <div style={{ height: 28, width: "70%", background: T.line, borderRadius: 8, marginTop: 22 }} />
            <div style={{ height: 14, width: "40%", background: T.line, borderRadius: 6, marginTop: 12 }} />
            <div style={{ height: 12, width: "50%", background: T.line, borderRadius: 6, marginTop: 18 }} />
            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                <div style={{ height: 44, width: 200, background: T.line, borderRadius: 14 }} />
                <div style={{ height: 44, width: 200, background: T.line, borderRadius: 14 }} />
            </div>
            <div style={{ height: 12, width: "30%", background: T.line, borderRadius: 6, marginTop: 22 }} />
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
                {[100, 95, 88, 92, 80].map((w, i) => (
                    <div key={i} style={{ height: 12, width: `${w}%`, background: T.line, borderRadius: 6 }} />
                ))}
            </div>
            <p style={{ fontSize: 12, color: T.ink3, textAlign: "center", marginTop: 24 }}>
                Concierge® is curating this route…
            </p>
        </div>
    );
}

function EscapeDetailDrawer({ open, productCode, onClose, onRequestConcierge }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeImage, setActiveImage] = useState(0);

    useEffect(() => {
        if (!open || !productCode) return;
        let cancelled = false;
        setLoading(true);
        setDetail(null);
        setActiveImage(0);
        getEscapeProduct(productCode)
            .then((d) => { if (!cancelled) setDetail(d); })
            .catch(() => { if (!cancelled) setDetail({ outage: true }); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, productCode]);

    if (!open) return null;

    function handleContinueToBooking() {
        if (!detail?.affiliate_url) return;
        if (detail?.url_status && detail.url_status !== "valid") return;
        // Open IMMEDIATELY · log fire-and-forget so we never block.
        window.open(detail.affiliate_url, "_blank", "noopener,noreferrer");
        logEscapeIntent({
            rail: "viator",
            entityId: detail.product_code,
            entitySnapshot: {
                title: detail.title,
                destination_name: detail.destination_name,
                country: detail.country,
                source_product_code: detail.source_product_code,
                url_status: detail.url_status,
            },
        });
    }
    function handleRequestConcierge() {
        // Founder ruling 18 Feb 2026 PM-6 · open the in-app
        // Concierge® request form (same shape as /enjoy's) instead
        // of bouncing the member to /connect. The form posts to
        // /api/escape/request → Zoho action-required ticket.
        onRequestConcierge?.({
            rail: "viator",
            product_code: detail?.product_code || productCode,
            title: detail?.title || "",
            destination: detail?.destination_name || "",
            country: detail?.country || "",
        });
    }

    return (
        <div
            role="dialog"
            aria-label="Experience details"
            data-testid="escape-drawer"
            style={{
                position: "fixed", inset: 0, zIndex: 60,
                background: "rgba(10,10,12,0.55)",
                display: "flex", justifyContent: "center",
                alignItems: "flex-start",   // ── founder bug fix · without this
                                            // the inner card stretched to viewport
                                            // height and clipped overflow content
                padding: "40px 22px",
                overflowY: "auto",
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <div style={{
                width: "min(960px, 100%)",
                maxHeight: "calc(100vh - 80px)",
                overflowY: "auto",          // ── inner card scrolls internally
                background: T.card,
                borderRadius: 18,
                border: `1px solid ${T.line2}`,
                position: "relative",
            }}>
                <div style={{
                    position: "sticky", top: 0, zIndex: 2,
                    display: "flex", justifyContent: "flex-end",
                    padding: 14,
                    background: "linear-gradient(to bottom, rgba(253,250,243,1) 60%, rgba(253,250,243,0))",
                }}>
                    <button
                        data-testid="escape-drawer-close"
                        onClick={onClose}
                        style={{ background: T.card, border: `1px solid ${T.line2}`, borderRadius: 999, width: 36, height: 36, fontSize: 20, color: T.ink2, cursor: "pointer" }}
                    >×</button>
                </div>
                {loading && <DrawerSkeleton />}
                {!loading && detail?.outage && (
                    <div data-testid="escape-drawer-outage" style={{ padding: "40px 32px 60px", textAlign: "center" }}>
                        <p style={{ fontSize: 16, color: T.ink2 }}>
                            We could not load live details right now.
                        </p>
                        <p style={{ fontSize: 14, color: T.ink3, marginTop: 6 }}>
                            The Concierge® can still help.
                        </p>
                        <button
                            onClick={handleRequestConcierge}
                            data-testid="escape-drawer-outage-request"
                            style={{
                                marginTop: 16, background: T.ink, color: T.card,
                                border: 0, borderRadius: 14, padding: "12px 22px",
                                fontSize: 14, cursor: "pointer",
                            }}
                        >Request via Concierge®</button>
                    </div>
                )}
                {!loading && detail && !detail.outage && (
                    <div style={{ padding: "0 32px 40px" }}>
                        {detail.images?.length > 0 && (
                            <div style={{ marginBottom: 22 }}>
                                <div
                                    data-testid="escape-drawer-hero"
                                    style={{
                                        width: "100%", aspectRatio: "16/9",
                                        background: `url(${detail.images[activeImage]}) center/cover no-repeat`,
                                        borderRadius: 12,
                                    }}
                                />
                                {detail.images.length > 1 && (
                                    <div style={{ display: "flex", gap: 8, marginTop: 8, overflowX: "auto" }}>
                                        {detail.images.slice(0, 6).map((url, i) => (
                                            <button
                                                key={url + i}
                                                onClick={() => setActiveImage(i)}
                                                data-testid={`escape-drawer-thumb-${i}`}
                                                style={{
                                                    width: 80, height: 56,
                                                    background: `url(${url}) center/cover no-repeat`,
                                                    border: i === activeImage ? `2px solid ${T.brass}` : `1px solid ${T.line2}`,
                                                    borderRadius: 8, padding: 0, cursor: "pointer",
                                                }}
                                                aria-label={`Image ${i + 1}`}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        <div data-testid="escape-drawer-titleblock">
                            <h2 className="t-serif" style={{ fontSize: 28, margin: 0, lineHeight: 1.15 }}>
                                {detail.title}
                            </h2>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 10, color: T.ink2, fontSize: 14 }}>
                                {(detail.destination_name || detail.country) && (
                                    <span>{[detail.destination_name, detail.country].filter(Boolean).join(" · ")}</span>
                                )}
                                {detail.rating != null && (
                                    <span>★ {detail.rating.toFixed(1)} {detail.review_count != null && `(${detail.review_count.toLocaleString("en-IN")})`}</span>
                                )}
                                {detail.duration_text && <span>⌖ {detail.duration_text}</span>}
                            </div>
                            {detail.from_price && (
                                <div style={{ marginTop: 12, fontSize: 16 }}>
                                    From <strong>{fmtPrice(detail.from_price)}</strong>
                                    <span style={{ color: T.ink3, fontSize: 12, marginLeft: 10 }}>
                                        Live price · final amount confirmed at checkout
                                    </span>
                                </div>
                            )}
                            {!detail.from_price && (
                                <div style={{ marginTop: 12, fontSize: 13, color: T.ink3 }}>
                                    Price available on partner site
                                </div>
                            )}
                        </div>

                        <div style={{ display: "flex", gap: 10, marginTop: 22, flexWrap: "wrap" }}>
                            {/* Founder ruling 18 Feb 2026 PM-10 · NEVER render
                                the direct-book CTA when url_status != valid.
                                Members must not land on a Viator 404. The
                                Concierge® route covers the rest. */}
                            {detail.url_status === "valid" && detail.affiliate_url && (
                                <button
                                    data-testid="escape-drawer-book-viator"
                                    onClick={handleContinueToBooking}
                                    style={{
                                        background: T.ink, color: T.card, border: 0,
                                        borderRadius: 14, padding: "13px 22px",
                                        fontSize: 14, fontWeight: 500, cursor: "pointer",
                                    }}
                                >
                                    Continue to booking ↗
                                </button>
                            )}
                            <button
                                data-testid="escape-drawer-request-concierge"
                                onClick={handleRequestConcierge}
                                style={{
                                    background: detail.url_status === "valid" ? "transparent" : T.ink,
                                    color: detail.url_status === "valid" ? T.ink : T.card,
                                    border: `1.5px solid ${T.ink}`, borderRadius: 14,
                                    padding: "13px 22px", fontSize: 14, fontWeight: 500, cursor: "pointer",
                                }}
                            >
                                Request via Concierge®
                            </button>
                        </div>
                        {detail.url_status !== "valid" && (
                            <p
                                data-testid="escape-drawer-partner-route-unavailable"
                                style={{
                                    fontSize: 12.5, color: T.ink3, marginTop: 10,
                                    fontStyle: "italic", lineHeight: 1.5,
                                }}
                            >
                                Partner booking route unavailable. The Concierge® can help.
                            </p>
                        )}
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: T.ink3, marginTop: 10 }}>
                            <span>Checkout is completed on the partner site.</span>
                            <span style={{ letterSpacing: "0.16em", textTransform: "uppercase" }}>Source · Viator</span>
                        </div>

                        {detail.description && (
                            <SectionDrawer title="Overview">
                                <p style={{ fontSize: 14, color: T.ink2, lineHeight: 1.55 }}>{detail.description}</p>
                            </SectionDrawer>
                        )}

                        {detail.highlights?.length > 0 && (
                            <SectionDrawer title="Highlights">
                                <ul style={{ paddingLeft: 18, margin: 0, color: T.ink2, fontSize: 14, lineHeight: 1.6 }}>
                                    {detail.highlights.map((h, i) => <li key={i}>{h}</li>)}
                                </ul>
                            </SectionDrawer>
                        )}

                        <div style={{ display: "grid", gap: 22, gridTemplateColumns: "1fr 1fr", marginTop: 18 }}>
                            {detail.inclusions?.length > 0 && (
                                <SectionDrawer title="What's included">
                                    <ul style={{ paddingLeft: 18, margin: 0, color: T.ink2, fontSize: 13, lineHeight: 1.55 }}>
                                        {detail.inclusions.map((x, i) => <li key={i}>{x}</li>)}
                                    </ul>
                                </SectionDrawer>
                            )}
                            {detail.exclusions?.length > 0 && (
                                <SectionDrawer title="Not included">
                                    <ul style={{ paddingLeft: 18, margin: 0, color: T.ink2, fontSize: 13, lineHeight: 1.55 }}>
                                        {detail.exclusions.map((x, i) => <li key={i}>{x}</li>)}
                                    </ul>
                                </SectionDrawer>
                            )}
                        </div>

                        {(detail.meeting_point || detail.cancellation_policy || detail.age_notes || detail.accessibility_info) && (
                            <div style={{ display: "grid", gap: 18, gridTemplateColumns: "1fr 1fr", marginTop: 18 }}>
                                {detail.meeting_point && (
                                    <SectionDrawer title="Meeting point">
                                        <p style={{ fontSize: 13, color: T.ink2, lineHeight: 1.55 }}>{detail.meeting_point}</p>
                                    </SectionDrawer>
                                )}
                                {detail.cancellation_policy && (
                                    <SectionDrawer title="Cancellation">
                                        <p style={{ fontSize: 13, color: T.ink2, lineHeight: 1.55 }}>{detail.cancellation_policy}</p>
                                    </SectionDrawer>
                                )}
                                {detail.age_notes && (
                                    <SectionDrawer title="Age / fitness">
                                        <p style={{ fontSize: 13, color: T.ink2, lineHeight: 1.55 }}>{detail.age_notes}</p>
                                    </SectionDrawer>
                                )}
                                {detail.accessibility_info && (
                                    <SectionDrawer title="Accessibility">
                                        <p style={{ fontSize: 13, color: T.ink2, lineHeight: 1.55 }}>{detail.accessibility_info}</p>
                                    </SectionDrawer>
                                )}
                            </div>
                        )}

                        {detail.operator_name && (
                            <div style={{ fontSize: 12, color: T.ink3, marginTop: 22 }}>
                                Operator · {detail.operator_name}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function SectionDrawer({ title, children }) {
    return (
        <div style={{ marginTop: 20 }}>
            <div style={{ ...STYLES.label, marginBottom: 6 }}>{title}</div>
            {children}
        </div>
    );
}

function SignatureCardImage({ src, title }) {
    const [errored, setErrored] = React.useState(false);
    // v3.48 · Cloudinary delivery-side optimisation. `cldOpt` is a
    // no-op for non-Cloudinary URLs (Viator, Google Places etc.) so
    // it's safe to always apply.
    const optimised = cldOpt(src, { w: 720 });
    if (!src || errored) {
        const initial = (title || "C").trim().charAt(0).toUpperCase();
        return (
            <div
                style={{
                    width: "100%", aspectRatio: "16/10",
                    background: `linear-gradient(135deg, ${T.brassWash} 0%, #F4EFE6 100%)`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}
                aria-hidden="true"
            >
                <span
                    className="t-serif"
                    style={{
                        fontSize: 56, color: T.brass,
                        fontWeight: 500, opacity: 0.7, letterSpacing: "-0.04em",
                    }}
                >{initial}</span>
            </div>
        );
    }
    return (
        <>
            <div
                style={{
                    width: "100%", aspectRatio: "16/10",
                    background: `url(${optimised}) center/cover no-repeat`,
                }}
            />
            <img src={optimised} alt="" onError={() => setErrored(true)} style={{ display: "none" }} />
        </>
    );
}

/* ── Signature Escapes shelf · founder ruling 18 Feb 2026 PM-11 ──
 *  Member-facing only. The desk's internal copy is scrubbed
 *  server-side (escape_relevance.scrub_row_for_member) — `s.concierge_whisper`
 *  is already safe to render. Card badge: "Curated by Concierge®".
 *  Cap: 3 cards. Section hides entirely when `items` is empty.
 *  PM-14 · clicking the card body (anywhere except the Request CTA)
 *  opens the SignatureDetailDrawer with the rich member view. */
function SignatureShelf({
    items,
    title = "Signature Escapes",
    subline = "Rare journeys and private experiences, arranged by the Concierge®.",
    onRequest,
    onOpenDetail,
    testid = "escape-signature-row",
}) {
    if (!items || items.length === 0) return null;
    const capped = items.slice(0, 3);
    return (
        <section data-testid={testid} style={{ padding: "8px 22px 48px" }}>
            <div style={STYLES.container}>
                <div style={{ marginBottom: 14 }}>
                    <div style={{ ...STYLES.label, color: T.brass, marginBottom: 4 }}>
                        {title}
                    </div>
                    <div style={{ fontSize: 13.5, color: T.ink3 }}>
                        {subline}
                    </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
                    {capped.map((s) => (
                        <article
                            key={s.id}
                            data-testid={`escape-signature-card-${s.id}`}
                            onClick={() => onOpenDetail?.(s)}
                            style={{
                                background: T.card, border: `1.5px solid ${T.brassDim}`,
                                borderRadius: 14, overflow: "hidden",
                                display: "flex", flexDirection: "column",
                                cursor: "pointer",
                                transition: "transform 200ms ease, box-shadow 200ms ease",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "translateY(-2px)";
                                e.currentTarget.style.boxShadow = "0 10px 28px rgba(33,28,21,0.12)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow = "none";
                            }}
                        >
                            <div data-testid={`escape-signature-card-image-${s.id}`}>
                                <SignatureCardImage src={s.hero_image_url} title={s.title} />
                            </div>
                            <div style={{ padding: "12px 14px 18px" }}>
                                <h3
                                    className="t-serif"
                                    data-testid={`escape-signature-card-title-${s.id}`}
                                    style={{ fontSize: 16, margin: "0 0 6px", color: T.ink }}
                                >{s.title}</h3>
                                <div style={{ fontSize: 12, color: T.ink3 }}>
                                    {[s.destination, s.country].filter(Boolean).join(" · ")}
                                </div>
                                {s.concierge_whisper && (
                                    <p
                                        className="t-serif"
                                        data-testid={`escape-signature-card-whisper-${s.id}`}
                                        style={{
                                            fontSize: 13.5, color: T.ink2,
                                            fontStyle: "italic", marginTop: 10, lineHeight: 1.45,
                                            display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                        }}
                                    >{s.concierge_whisper}</p>
                                )}
                                <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <span
                                        data-testid={`escape-signature-card-badge-${s.id}`}
                                        style={{
                                            fontSize: 10, letterSpacing: "0.16em",
                                            textTransform: "uppercase", color: T.brass,
                                        }}
                                    >Curated by Concierge®</span>
                                    <button
                                        data-testid={`escape-signature-request-${s.id}`}
                                        onClick={(e) => { e.stopPropagation(); onRequest?.(s); }}
                                        style={{
                                            fontSize: 12, color: T.ink, border: `1px solid ${T.ink}`,
                                            background: "transparent", borderRadius: 999,
                                            padding: "6px 12px", cursor: "pointer", fontFamily: "inherit",
                                        }}
                                    >Request via Concierge®</button>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ── Page ─────────────────────────────────────────────────────── */
export default function EscapePage() {
    const navigate = useNavigate();
    const [q, setQ] = useState("");
    const [submitted, setSubmitted] = useState("");
    const [dateValue, setDateValue] = useState({ mode: "anytime", startDate: null, endDate: null });
    const [travellers, setTravellers] = useState({ adults: 2, children: 0 });
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [outage, setOutage] = useState(false);
    const [droppedCount, setDroppedCount] = useState(0);
    // Pagination · founder ruling 19 Feb 2026 "no pagination is coming on
    // anything". Track the server's `next_start` cursor + has_more flag.
    // Viator caps `count` at 50 · page size 24 fits the 4-col grid neatly.
    const PAGE_SIZE = 24;
    const [nextStart, setNextStart] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [totalAvailable, setTotalAvailable] = useState(0);
    const [loadingMore, setLoadingMore] = useState(false);

    const [refineOpen, setRefineOpen] = useState(false);
    const [refineApplied, setRefineApplied] = useState(EMPTY_REFINE);
    const [refineDraft, setRefineDraft] = useState(EMPTY_REFINE);

    // ── Founder UX pass 18 Feb 2026 PM-9 · global market controls
    //    Currency is persisted (localStorage) so a member's choice
    //    sticks across sessions. Default INR per founder ruling.
    const [currency, setCurrency] = useState(() => {
        try {
            return window.localStorage.getItem(CURRENCY_LS_KEY) || "INR";
        } catch { return "INR"; }
    });
    useEffect(() => {
        try { window.localStorage.setItem(CURRENCY_LS_KEY, currency); }
        catch { /* localStorage unavailable */ }
    }, [currency]);

    // ── Sort order · default "Recommended" (Viator DEFAULT).      */
    const [sortId, setSortId] = useState("RECOMMENDED");

    // ── Quick-chip toolbar selections (member-facing labels)       */
    const [activeQuickChipLabels, setActiveQuickChipLabels] = useState([]);

    // ── Active-search Signature shelf (relevance-gated · max 3).
    // Founder ruling 18 Feb 2026 PM-11: NEVER show generic
    // Signature Escapes above or alongside an active search query.
    // Only relevant rows surface, only BELOW results. When the
    // backend returns zero matches, the shelf hides entirely.
    const [activeSignatures, setActiveSignatures] = useState([]);

    useEffect(() => {
        if (!submitted) {
            setActiveSignatures([]);
            return;
        }
        let cancelled = false;
        getEscapeSignatures({ q: submitted, limit: 3 })
            .then((r) => { if (!cancelled) setActiveSignatures(r.results || []); })
            .catch(() => { if (!cancelled) setActiveSignatures([]); });
        return () => { cancelled = true; };
    }, [submitted]);

    const [signatures, setSignatures] = useState([]);
    const [picked, setPicked] = useState(null);
    const [pickedSignature, setPickedSignature] = useState(null);
    const [discoverOpen, setDiscoverOpen] = useState(false);
    const [requestCtx, setRequestCtx] = useState(null); // {rail, product_code|signature_id, title, destination, country}
    const abortRef = useRef(null);

    useEffect(() => {
        getEscapeSignatures({ limit: 3 })
            .then((r) => setSignatures(r.results || []))
            .catch(() => setSignatures([]));
    }, []);

    const runSearch = useCallback(async (query, opts = {}) => {
        const text = (query ?? q).trim();
        if (!text) return;
        abortRef.current?.abort?.();
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        setLoading(true);
        setOutage(false);
        setResults([]);
        // Reset pagination for a fresh search
        setNextStart(null);
        setHasMore(false);
        setTotalAvailable(0);
        setSubmitted(text);
        const refine = opts.refine ?? refineApplied;
        const dv = opts.dateValue ?? dateValue;
        const cur = opts.currency ?? currency;
        const chipLabels = opts.quickChips ?? activeQuickChipLabels;
        const sId = opts.sortId ?? sortId;

        // Quick chips fall into two lanes:
        //   · "flag" chips push into refine.flags for THIS search
        //   · "term" chips concat to the searchTerm (Viator freetext)
        const chipDefs = chipLabels
            .map((lbl) => QUICK_CHIPS.find((c) => c.label === lbl))
            .filter(Boolean);
        const chipFlags = chipDefs.filter((c) => c.kind === "flag").map((c) => c.value);
        const chipTerms = chipDefs.filter((c) => c.kind === "term").map((c) => c.value);
        // Wave B-2 · refine.region + refine.landmark fold into the
        // freetext query as anchor tokens so Viator narrows server-
        // side. We don't pass them as separate params (no Viator
        // taxonomy mapping yet) · doctrine: never fabricate IDs.
        const refineQ = [
            refine.region ? REGION_OPTIONS.find((r) => r.id === refine.region)?.label : null,
            refine.landmark,
        ].filter(Boolean).join(" ");
        // Family / infant prefs also fold in as terms — Viator's
        // freetext engine catches "family", "kid-friendly", "infant"
        // tokens reliably. Avoids inventing taxonomy.
        const traveller_terms = [
            refine.family_friendly ? "family" : null,
            refine.infants_welcome ? "infant" : null,
        ].filter(Boolean).join(" ");
        const combinedQ = [text, refineQ, traveller_terms, chipTerms.join(" ")]
            .filter(Boolean).join(" ").trim();
        const combinedFlags = Array.from(new Set([...(refine.flags || []), ...chipFlags]));

        // Resolve sort option to backend params
        const sortDef = SORT_OPTIONS.find((s) => s.id === sId) || SORT_OPTIONS[0];
        const params = {
            q: combinedQ,
            start: 1,
            count: PAGE_SIZE,
            currency: cur,
            language: refine.language || "en-US",
            include_unrated: refine.include_unrated ? "true" : "false",
            sort: sortDef.sort,
            order: sortDef.order,
        };
        if (dv && dv.mode !== "anytime" && dv.startDate && dv.endDate) {
            params.start_date = dv.startDate;
            params.end_date = dv.endDate;
        }
        if (combinedFlags.length) params.flags = combinedFlags.join(",");
        if (refine.duration_min != null) params.duration_min = refine.duration_min;
        if (refine.duration_max != null) params.duration_max = refine.duration_max;
        if (refine.rating_min != null) params.rating_min = refine.rating_min;
        if (refine.lowest_price != null) params.lowest_price = refine.lowest_price;
        if (refine.highest_price != null) params.highest_price = refine.highest_price;
        try {
            const data = await searchEscape(params);
            if (ctrl.signal.aborted) return;
            setResults(data.results || []);
            setOutage(!!data.outage);
            setDroppedCount(data.dropped_by_quality_floor || 0);
            setNextStart(data.next_start ?? null);
            setHasMore(!!data.has_more);
            setTotalAvailable(data.total_count || 0);
        } catch (e) {
            if (ctrl.signal.aborted) return;
            setOutage(true);
        } finally {
            if (!ctrl.signal.aborted) setLoading(false);
        }
    }, [q, refineApplied, dateValue, currency, activeQuickChipLabels, sortId]);

    const loadMore = useCallback(async () => {
        if (!submitted || !hasMore || !nextStart || loadingMore) return;
        setLoadingMore(true);
        try {
            const refine = refineApplied;
            const chipDefs = activeQuickChipLabels
                .map((lbl) => QUICK_CHIPS.find((c) => c.label === lbl))
                .filter(Boolean);
            const chipFlags = chipDefs.filter((c) => c.kind === "flag").map((c) => c.value);
            const chipTerms = chipDefs.filter((c) => c.kind === "term").map((c) => c.value);
            const refineQ = [
                refine.region ? REGION_OPTIONS.find((r) => r.id === refine.region)?.label : null,
                refine.landmark,
            ].filter(Boolean).join(" ");
            const traveller_terms = [
                refine.family_friendly ? "family" : null,
                refine.infants_welcome ? "infant" : null,
            ].filter(Boolean).join(" ");
            const combinedQ = [submitted, refineQ, traveller_terms, chipTerms.join(" ")]
                .filter(Boolean).join(" ").trim();
            const combinedFlags = Array.from(new Set([...(refine.flags || []), ...chipFlags]));
            const sortDef = SORT_OPTIONS.find((s) => s.id === sortId) || SORT_OPTIONS[0];
            const params = {
                q: combinedQ,
                start: nextStart,
                count: PAGE_SIZE,
                currency,
                language: refine.language || "en-US",
                include_unrated: refine.include_unrated ? "true" : "false",
                sort: sortDef.sort,
                order: sortDef.order,
            };
            if (dateValue && dateValue.mode !== "anytime" && dateValue.startDate && dateValue.endDate) {
                params.start_date = dateValue.startDate;
                params.end_date = dateValue.endDate;
            }
            if (combinedFlags.length) params.flags = combinedFlags.join(",");
            if (refine.duration_min != null) params.duration_min = refine.duration_min;
            if (refine.duration_max != null) params.duration_max = refine.duration_max;
            if (refine.rating_min != null) params.rating_min = refine.rating_min;
            if (refine.lowest_price != null) params.lowest_price = refine.lowest_price;
            if (refine.highest_price != null) params.highest_price = refine.highest_price;
            const data = await searchEscape(params);
            // De-duplicate by product_code in case the API window overlaps.
            setResults((prev) => {
                const seen = new Set(prev.map((c) => c.product_code));
                const incoming = (data.results || []).filter((c) => !seen.has(c.product_code));
                return [...prev, ...incoming];
            });
            setNextStart(data.next_start ?? null);
            setHasMore(!!data.has_more);
            setTotalAvailable(data.total_count || 0);
        } catch {
            // soft-fail · keep what we have, let the user retry
        } finally {
            setLoadingMore(false);
        }
    }, [submitted, hasMore, nextStart, loadingMore, refineApplied, activeQuickChipLabels, sortId, currency, dateValue]);

    function onApplyRefine() {
        setRefineApplied(refineDraft);
        if (submitted) runSearch(submitted, { refine: refineDraft });
    }
    function onClearRefine() {
        setRefineDraft(EMPTY_REFINE);
        setRefineApplied(EMPTY_REFINE);
        if (submitted) runSearch(submitted, { refine: EMPTY_REFINE });
    }

    const refineActiveCount = useMemo(() => {
        let n = 0;
        if ((refineApplied.flags || []).length) n += refineApplied.flags.length;
        if ((refineApplied.categories || []).length) n += refineApplied.categories.length;
        if (refineApplied.duration_min != null || refineApplied.duration_max != null) n += 1;
        if (refineApplied.rating_min != null) n += 1;
        if (refineApplied.lowest_price != null || refineApplied.highest_price != null) n += 1;
        if (refineApplied.include_unrated) n += 1;
        if (refineApplied.language) n += 1;
        return n;
    }, [refineApplied]);

    // Active filter chips (excluding quick chips, which the toolbar
    // renders separately so they can co-exist with refine state).
    const activeFilterChips = useMemo(() => {
        const out = [];
        (refineApplied.flags || []).forEach((f) => {
            const flagDef = FLAGS_AVAILABLE.find((x) => x.id === f);
            out.push({
                key: `flag-${f}`,
                label: flagDef ? flagDef.label : f.replace(/_/g, " ").toLowerCase(),
                onRemove: () => {
                    const next = { ...refineApplied, flags: (refineApplied.flags || []).filter((x) => x !== f) };
                    setRefineApplied(next);
                    setRefineDraft(next);
                    if (submitted) runSearch(submitted, { refine: next });
                },
            });
        });
        (refineApplied.categories || []).forEach((cat) => {
            out.push({
                key: `cat-${cat.toLowerCase().replace(/[^a-z]+/g, "-")}`,
                label: cat,
                onRemove: () => {
                    const next = { ...refineApplied, categories: (refineApplied.categories || []).filter((x) => x !== cat) };
                    setRefineApplied(next);
                    setRefineDraft(next);
                    if (submitted) runSearch(submitted, { refine: next });
                },
            });
        });
        if (refineApplied.rating_min != null) {
            const ratingLabel = `★ ${refineApplied.rating_min}+`;
            out.push({
                key: "rating",
                label: ratingLabel,
                onRemove: () => {
                    const next = { ...refineApplied, rating_min: null };
                    setRefineApplied(next);
                    setRefineDraft(next);
                    if (submitted) runSearch(submitted, { refine: next });
                },
            });
        }
        if (refineApplied.duration_min != null || refineApplied.duration_max != null) {
            const dp = DURATION_PRESETS.find(
                (p) => p.min === refineApplied.duration_min && p.max === refineApplied.duration_max,
            );
            out.push({
                key: "duration",
                label: dp ? dp.label : "Duration",
                onRemove: () => {
                    const next = { ...refineApplied, duration_min: null, duration_max: null };
                    setRefineApplied(next);
                    setRefineDraft(next);
                    if (submitted) runSearch(submitted, { refine: next });
                },
            });
        }
        if (refineApplied.lowest_price != null || refineApplied.highest_price != null) {
            const pp = PRICE_PRESETS.find(
                (p) => p.lowest === refineApplied.lowest_price && p.highest === refineApplied.highest_price,
            );
            out.push({
                key: "price",
                label: pp ? pp.label : "Price",
                onRemove: () => {
                    const next = { ...refineApplied, lowest_price: null, highest_price: null };
                    setRefineApplied(next);
                    setRefineDraft(next);
                    if (submitted) runSearch(submitted, { refine: next });
                },
            });
        }
        if (refineApplied.language) {
            const lng = LANGUAGE_OPTIONS.find((l) => l.code === refineApplied.language);
            out.push({
                key: "language",
                label: lng ? lng.label : refineApplied.language,
                onRemove: () => {
                    const next = { ...refineApplied, language: null };
                    setRefineApplied(next);
                    setRefineDraft(next);
                    if (submitted) runSearch(submitted, { refine: next });
                },
            });
        }
        return out;
    }, [refineApplied, runSearch, submitted]);

    function toggleQuickChip(chip) {
        const next = activeQuickChipLabels.includes(chip.label)
            ? activeQuickChipLabels.filter((l) => l !== chip.label)
            : [...activeQuickChipLabels, chip.label];
        setActiveQuickChipLabels(next);
        if (submitted) runSearch(submitted, { quickChips: next });
    }

    function onSortChange(sortDef) {
        setSortId(sortDef.id);
        if (submitted) runSearch(submitted, { sortId: sortDef.id });
    }

    function clearAllActiveFilters() {
        setActiveQuickChipLabels([]);
        setRefineApplied(EMPTY_REFINE);
        setRefineDraft(EMPTY_REFINE);
        if (submitted) runSearch(submitted, { refine: EMPTY_REFINE, quickChips: [] });
    }

    function handleCurrencyChange(code) {
        setCurrency(code);
        if (submitted) runSearch(submitted, { currency: code });
    }

    return (
        <div
            style={STYLES.page}
            data-testid="escape-page"
            data-table-surface
            className="t-grain lc-surface-with-float-pill"
        >
            <TopBar currency={currency} onCurrencyChange={handleCurrencyChange} />

            {/* Continuity Spine — soft cross-pillar whisper. Silo doctrine:
                Dreamweaver never feeds this band. Anonymous visitors see
                the default "essence" whisper for /escape. */}
            <MemberContextBand pillar="escape" />

            {/* Landing hero · only when no search has run. After search,
                the compact ResultsSearchStrip + Toolbar take over so the
                results-first feel matches /enjoy's "show me the night"
                rhythm. */}
            {!submitted && (
                <main className="max-w-6xl mx-auto px-6">
                    <HeroSearch
                        q={q}
                        setQ={setQ}
                        dateValue={dateValue}
                        setDateValue={(v) => {
                            setDateValue(v);
                            if (submitted) runSearch(submitted, { dateValue: v });
                        }}
                        travellers={travellers}
                        setTravellers={setTravellers}
                        loading={loading}
                        onSubmit={() => runSearch()}
                        onOpenDiscover={() => setDiscoverOpen(true)}
                        onAskConcierge={() => setRequestCtx({
                            rail: "signature",
                            title: q.trim() || "(general escape ask)",
                            destination: q.trim() || null,
                            country: null,
                        })}
                    />
                </main>
            )}

            {/* Post-search · compact strip carries the search/date/travellers */}
            {submitted && (
                <ResultsSearchStrip
                    q={q}
                    setQ={setQ}
                    dateValue={dateValue}
                    setDateValue={(v) => {
                        setDateValue(v);
                        if (submitted) runSearch(submitted, { dateValue: v });
                    }}
                    travellers={travellers}
                    setTravellers={setTravellers}
                    onSubmit={() => runSearch()}
                    loading={loading}
                />
            )}

            {/* Top destinations · founder ruling 18 Feb 2026 PM-6 ·
                show only on the landing screen (pre-search). Once
                a search has run, the results carry the page. */}
            {!submitted && !loading && (
                <EscapeTopDestinations
                    onPick={(query) => { setQ(query); runSearch(query); }}
                />
            )}

            {!submitted && (
                <SignatureShelf
                    items={signatures}
                    title="Signature Escapes"
                    subline="Rare journeys and private experiences, arranged by the Concierge®."
                    testid="escape-signature-row-landing"
                    onOpenDetail={(s) => setPickedSignature(s.id)}
                    onRequest={(s) => setRequestCtx({
                        rail: "signature",
                        signature_id: s.id,
                        title: s.title || "",
                        destination: s.destination || "",
                        country: s.country || "",
                    })}
                />
            )}

            {!submitted && !loading && (
                <PopularPrompts onPick={(p) => { setQ(p); runSearch(p); }} />
            )}

            {!submitted && <EscapeFAQ />}

            {submitted && (
                <section data-testid="escape-results" style={{ padding: "20px 22px 80px" }}>
                    <div style={STYLES.container}>
                        <EscapeResultsToolbar
                            resultCount={results.length}
                            queryLabel={submitted}
                            sortId={sortId}
                            onSortChange={onSortChange}
                            onRefineOpen={() => { setRefineDraft(refineApplied); setRefineOpen(true); }}
                            refineActiveCount={refineActiveCount}
                            activeQuickChips={activeQuickChipLabels}
                            onToggleQuickChip={toggleQuickChip}
                            activeFilterChips={activeFilterChips}
                            onClearAllFilters={clearAllActiveFilters}
                            loading={loading}
                        />

                        {loading && <SkeletonGrid />}

                        {!loading && outage && (
                            <div data-testid="escape-empty-outage" style={{ padding: "40px 12px", textAlign: "center", color: T.ink2 }}>
                                <p style={{ fontSize: 16 }}>We could not load live experiences right now.</p>
                                <p style={{ fontSize: 14, color: T.ink3, marginTop: 6 }}>The Concierge® can still help.</p>
                                <button
                                    data-testid="escape-empty-outage-request"
                                    onClick={() => setRequestCtx({
                                        rail: "viator",
                                        title: submitted || "(general escape enquiry)",
                                    })}
                                    style={{ marginTop: 16, background: T.ink, color: T.card, border: 0, borderRadius: 14, padding: "12px 22px", fontSize: 14, cursor: "pointer" }}
                                >Ask the Concierge®</button>
                            </div>
                        )}

                        {!loading && !outage && results.length === 0 && (
                            <div data-testid="escape-empty-noresults" style={{ padding: "32px 12px", textAlign: "center", color: T.ink2 }}>
                                {/* Founder ruling 18 Feb 2026 PM-11 · no-result must
                                    NOT fall back to random Signature Escapes. */}
                                <p
                                    data-testid="escape-empty-noresults-line"
                                    className="t-serif"
                                    style={{ fontSize: 16, lineHeight: 1.5, color: T.ink2 }}
                                >
                                    We could not find an exact match for{" "}
                                    <em style={{ color: T.ink }}>&ldquo;{submitted}&rdquo;</em>,
                                    but the Concierge® can help route this.
                                </p>
                                <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 18 }}>
                                    <button
                                        data-testid="escape-empty-noresults-request"
                                        onClick={() => setRequestCtx({
                                            rail: "viator",
                                            title: submitted || "(general escape enquiry)",
                                        })}
                                        style={{ background: T.ink, color: T.card, border: 0, borderRadius: 14, padding: "10px 18px", fontSize: 13, cursor: "pointer" }}
                                    >Ask the Concierge®</button>
                                    <button
                                        data-testid="escape-empty-noresults-clear"
                                        onClick={() => { setQ(""); setSubmitted(""); setResults([]); setActiveQuickChipLabels([]); }}
                                        style={{ background: "transparent", color: T.ink, border: `1px solid ${T.ink}`, borderRadius: 14, padding: "10px 18px", fontSize: 13, cursor: "pointer" }}
                                    >Clear search</button>
                                </div>
                                {droppedCount > 0 && !refineApplied.include_unrated && (
                                    <p style={{ fontSize: 12, color: T.ink3, marginTop: 18 }}>
                                        {droppedCount} unrated / sub-3-star results were hidden by the quality floor.
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const next = { ...refineApplied, include_unrated: true };
                                                setRefineApplied(next);
                                                setRefineDraft(next);
                                                runSearch(submitted, { refine: next });
                                            }}
                                            style={{ background: "transparent", border: 0, color: T.brass, fontSize: 12, cursor: "pointer", textDecoration: "underline", marginLeft: 6 }}
                                        >Include them</button>
                                    </p>
                                )}
                            </div>
                        )}

                        {!loading && !outage && results.length > 0 && (
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 18 }}>
                                {results.map((c) => (
                                    <EscapeCard key={c.product_code} card={c} onOpen={(card) => setPicked(card)} />
                                ))}
                            </div>
                        )}

                        {/* Founder ruling 19 Feb 2026 · "no pagination is coming on anything".
                            Surface Viator's server-side total and a Load-more button when
                            more pages are available. Quiet typography matching the toolbar. */}
                        {!loading && !outage && results.length > 0 && (
                            <div
                                data-testid="escape-pagination-footer"
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 10,
                                    marginTop: 28,
                                    paddingTop: 18,
                                    borderTop: `1px solid ${T.line}`,
                                }}
                            >
                                <div
                                    data-testid="escape-pagination-counter"
                                    style={{ fontSize: 12, color: T.ink3, letterSpacing: 1, textTransform: "uppercase" }}
                                >
                                    Showing {results.length}
                                    {totalAvailable > results.length && (
                                        <> of {totalAvailable.toLocaleString("en-IN")} matches</>
                                    )}
                                </div>
                                {hasMore && (
                                    <button
                                        type="button"
                                        data-testid="escape-load-more"
                                        onClick={loadMore}
                                        disabled={loadingMore}
                                        style={{
                                            background: T.ink,
                                            color: T.card,
                                            border: 0,
                                            borderRadius: 999,
                                            padding: "11px 26px",
                                            fontSize: 13,
                                            letterSpacing: 1.2,
                                            textTransform: "uppercase",
                                            cursor: loadingMore ? "wait" : "pointer",
                                            opacity: loadingMore ? 0.6 : 1,
                                            transition: "opacity 200ms ease",
                                        }}
                                    >
                                        {loadingMore ? "Loading…" : "Load more"}
                                    </button>
                                )}
                                {!hasMore && results.length >= PAGE_SIZE && (
                                    <p
                                        data-testid="escape-pagination-end"
                                        style={{ fontSize: 12, color: T.ink3, fontStyle: "italic" }}
                                    >
                                        You&rsquo;ve reached the end of the live catalogue for this search.
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Active-search relevance-gated Signature shelf.
                        Renders ONLY when the backend's escape_relevance
                        gate returns matches for the query (e.g. "St Tropez"
                        → French Riviera-tagged rows). Hidden otherwise — never
                        random fallback. */}
                    {!loading && !outage && activeSignatures.length > 0 && (
                        <SignatureShelf
                            items={activeSignatures}
                            title="Concierge® Signature Escapes"
                            subline="Private routes the Concierge® can arrange around this request."
                            testid="escape-signature-row-active"
                            onOpenDetail={(s) => setPickedSignature(s.id)}
                            onRequest={(s) => setRequestCtx({
                                rail: "signature",
                                signature_id: s.id,
                                title: s.title || "",
                                destination: s.destination || "",
                                country: s.country || "",
                            })}
                        />
                    )}
                </section>
            )}

            <RefineDrawer
                open={refineOpen}
                draft={refineDraft}
                setDraft={setRefineDraft}
                onApply={onApplyRefine}
                onClear={onClearRefine}
                onClose={() => setRefineOpen(false)}
            />

            <EscapeDetailDrawer
                open={!!picked}
                productCode={picked?.product_code}
                onClose={() => setPicked(null)}
                onRequestConcierge={(ctx) => setRequestCtx(ctx)}
            />

            <SignatureDetailDrawer
                open={!!pickedSignature}
                signatureId={pickedSignature}
                onClose={() => setPickedSignature(null)}
                onRequest={(row) => {
                    setPickedSignature(null);
                    setRequestCtx({
                        rail: "signature",
                        signature_id: row.id,
                        title: row.title || "",
                        destination: row.destination || "",
                        country: row.country || "",
                    });
                }}
            />

            <EscapeDiscover
                open={discoverOpen}
                onClose={() => setDiscoverOpen(false)}
                onPick={(query) => {
                    setQ(query);
                    runSearch(query);
                }}
            />

            <EscapeRequestModal
                open={!!requestCtx}
                context={requestCtx}
                onClose={() => setRequestCtx(null)}
                onSubmitted={() => { /* keep modal open until member taps Done */ }}
            />

            <footer style={{ borderTop: `1px solid ${T.line}`, padding: "24px 22px", color: T.ink3, fontSize: 12 }}>
                <div style={STYLES.container}>
                    /escape · Concierge® calm · open catalogue powered by Viator · checkout completed on the partner site.
                </div>
            </footer>

            <ConciergeWhatsAppPill context="escape" />
        </div>
    );
}

function SkeletonGrid() {
    return (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 18 }} data-testid="escape-skeleton">
            {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: 14, overflow: "hidden" }}>
                    <div style={{ aspectRatio: "16/10", background: T.line }} />
                    <div style={{ padding: 16 }}>
                        <div style={{ height: 14, background: T.line, borderRadius: 6, marginBottom: 8 }} />
                        <div style={{ height: 10, background: T.line, borderRadius: 6, width: "60%" }} />
                    </div>
                </div>
            ))}
        </div>
    );
}
